import { useState, useEffect, useCallback, useRef } from 'react';

const BASE = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:3001';
const POLL_INTERVAL = Number(process.env.REACT_APP_POLL_INTERVAL_MS) || 300_000;

function buildUrl(path, params = {}) {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') url.searchParams.set(k, v);
  });
  return url.toString();
}

export function useApi(path, params = {}, { poll = false, enabled = true } = {}) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const timerRef              = useRef(null);
  // Race-Guard: wenn Zeitraum/Filter schnell hintereinander wechseln, koennen
  // mehrere Requests parallel unterwegs sein — ohne das hier wuerde die
  // ZULETZT ANTWORTENDE (nicht die zuletzt GESTARTETE) Anfrage gewinnen und
  // kurz falsche/veraltete Daten anzeigen. reqIdRef zaehlt hoch, nur die
  // Antwort mit der aktuellen ID wird uebernommen.
  const reqIdRef               = useRef(0);
  const paramsKey             = JSON.stringify(params);

  const fetch_ = useCallback(async () => {
    const myReqId = ++reqIdRef.current;
    try {
      const res = await fetch(buildUrl(path, JSON.parse(paramsKey)));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (myReqId !== reqIdRef.current) return; // veraltete Antwort — verwerfen
      setData(json);
      setError(null);
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;
      setError(e.message);
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [path, paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // enabled=false (z.B. eingeklappte Sektion): gar nicht erst laden — kein
    // "haengender" Ladezustand, der den globalen Ladebalken faelschlich
    // dauerhaft aktiv haelt (Lazy-Load-Fix 2026-07-21).
    if (!enabled) { setLoading(false); return; }
    setLoading(true);
    fetch_();
    if (poll) {
      timerRef.current = setInterval(fetch_, POLL_INTERVAL);
    }
    return () => clearInterval(timerRef.current);
  }, [fetch_, poll, enabled]);

  return { data, loading, error, refetch: fetch_ };
}

export function useStats(params) {
  return useApi('/api/stats', params, { poll: true });
}

export function useCalls(params) {
  return useApi('/api/calls', params, { poll: true });
}

export function useAgents() {
  return useApi('/api/agents', {}, { poll: false });
}
